import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Activity, Menu, X, ArrowRight } from "lucide-react";

export const IMG = {
  hero: "https://images.unsplash.com/photo-1758691462749-a95ce1bd7f96?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  team: "https://images.pexels.com/photos/5452189/pexels-photo-5452189.jpeg?auto=compress&cs=tinysrgb&w=1400",
  data: "https://images.pexels.com/photos/5452285/pexels-photo-5452285.jpeg?auto=compress&cs=tinysrgb&w=1400",
  infusion: "https://images.pexels.com/photos/28318746/pexels-photo-28318746.jpeg?auto=compress&cs=tinysrgb&w=1400",
  pharmacy: "https://images.pexels.com/photos/30338195/pexels-photo-30338195.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

export const PUBLIC_NAV = [
  { to: "/platform", label: "Platform" },
  { to: "/solutions", label: "Solutions" },
  { to: "/referral-tracking", label: "Referral Tracking" },
  { to: "/how-it-works", label: "How It Works" },
];

export function Logo({ dark = false }) {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="public-logo">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white shadow-sm">
        <Activity className="h-5 w-5" />
      </span>
      <span className={`font-display text-xl font-extrabold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>
        Hudl<span className="text-primary">Up</span>
      </span>
    </Link>
  );
}

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors ${isActive ? "text-primary" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3.5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-7 md:flex">
          {PUBLIC_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`pubnav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/login" data-testid="pubnav-signin" className="text-sm font-semibold text-slate-700 hover:text-primary">Sign In</Link>
          <Link to="/book-demo" data-testid="pubnav-book-demo" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 active:scale-95">
            Book a Demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <button data-testid="pubnav-toggle" onClick={() => setOpen((o) => !o)} className="rounded-lg p-2 text-slate-700 md:hidden">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-3 md:hidden" data-testid="pubnav-mobile">
          <div className="flex flex-col gap-1">
            {PUBLIC_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">{n.label}</NavLink>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Sign In</Link>
            <Link to="/book-demo" onClick={() => setOpen(false)} className="mt-1 rounded-lg bg-primary px-3 py-2 text-center text-sm font-bold text-white">Book a Demo</Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-slate-800 bg-slate-900 text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 md:grid-cols-[1.4fr_1fr_1fr] lg:px-8">
        <div>
          <Logo dark />
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
            The CRM for 340B &amp; RPM with referral tracking — connecting provider relationships to service activation.
          </p>
        </div>
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Product</p>
          <ul className="space-y-2 text-sm">
            {PUBLIC_NAV.map((n) => <li key={n.to}><Link to={n.to} className="text-slate-400 hover:text-white">{n.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Get started</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/book-demo" className="text-slate-400 hover:text-white">Book a Demo</Link></li>
            <li><Link to="/login" className="text-slate-400 hover:text-white">Sign In</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 px-5 py-5 lg:px-8">
        <p className="mx-auto max-w-7xl text-xs leading-relaxed text-slate-500">
          © {new Date().getFullYear()} HudlUp. Product previews are illustrative. HudlUp is a relationship and workflow CRM;
          keep patient-identifying information and clinical documents in your organization's approved clinical system.
        </p>
      </div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <PublicNav />
      <main>{children}</main>
      <PublicFooter />
    </div>
  );
}

export function Eyebrow({ children }) {
  return <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-primary">{children}</p>;
}

export function SectionCTA() {
  return (
    <section className="bg-slate-900">
      <div className="mx-auto max-w-7xl px-5 py-16 text-center lg:px-8">
        <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">See HudlUp on your own workflow</h2>
        <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
          Connect provider acquisition, client onboarding, service activation, and referral follow-up in one workspace.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/book-demo" data-testid="cta-book-demo" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-base font-bold text-white transition-transform hover:-translate-y-0.5 active:scale-95">
            Book a Demo <ArrowRight className="h-5 w-5" />
          </Link>
          <Link to="/platform" data-testid="cta-explore" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-base font-bold text-white hover:bg-slate-800">
            Explore the Platform
          </Link>
        </div>
      </div>
    </section>
  );
}

// Illustrative product-preview window chrome (clearly labeled, no real data).
export function BrowserFrame({ children, label = "Illustrative preview" }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-900/5">
      <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        <span className="ml-3 text-[11px] font-semibold text-slate-400">{label}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const svcTone = {
  Active: "bg-emerald-100 text-emerald-700",
  Onboarding: "bg-amber-100 text-amber-700",
  "On Hold": "bg-slate-200 text-slate-600",
};

export function ClientServicesPreview() {
  const rows = [
    { org: "Riverside Internal Medicine", status: "DTCM Form Completed", services: [["340B", "Active"], ["RPM", "Onboarding"]] },
    { org: "Cardwell Cardiology Group", status: "Client is sending Infusion", services: [["Infusion", "Active"]] },
    { org: "Summit Family Practice", status: "Onboarding", services: [["RPM", "Onboarding"], ["340B", "On Hold"]] },
  ];
  return (
    <BrowserFrame label="Illustrative preview — Clients & Services">
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.org} className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-sm font-bold text-slate-800">{r.org}</p>
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">{r.status}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.services.map(([n, s]) => (
                <span key={n} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${svcTone[s] || "bg-slate-100 text-slate-500"}`}>{n} · {s}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </BrowserFrame>
  );
}

const refTone = {
  Submitted: "bg-slate-200 text-slate-600", Contacted: "bg-sky-100 text-sky-700",
  Accepted: "bg-indigo-100 text-indigo-700", "In Progress": "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700",
};
export function ReferralPreview() {
  const rows = [
    ["REF-00241", "Riverside IM", "340B", "Submitted"],
    ["REF-00240", "Cardwell Cardiology", "Infusion", "In Progress"],
    ["REF-00238", "Summit Family", "RPM", "Accepted"],
    ["REF-00235", "Lakeside Clinic", "340B", "Completed"],
  ];
  return (
    <BrowserFrame label="Illustrative preview — Referral Tracking">
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r[0]} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <span className="font-mono text-[11px] font-bold text-slate-400">{r[0]}</span>
            <span className="flex-1 truncate text-sm font-semibold text-slate-700">{r[1]}</span>
            <span className="hidden rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500 sm:inline">{r[2]}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${refTone[r[3]]}`}>{r[3]}</span>
          </div>
        ))}
      </div>
    </BrowserFrame>
  );
}
